// src/pages/Dashboard.tsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAllCompetitions } from "../firebase/competitions";
import { getCreditPackagesByCoach, CreditPackage } from "../firebase/creditPackages";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "../hooks/useTranslation";
import Navbar from "../components/Navbar";
import { calculatePrice } from "../utils/priceCalculator";
import { formatDateGeorgian } from "../utils/dateHelpers";
import { Timestamp } from "firebase/firestore";
import { C, FONT } from "../theme";

// ── Responsive hook ───────────────────────────────────────────────────────────
function useWindowWidth() {
  const [w, setW] = useState(window.innerWidth);
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return w;
}


interface Competition {
  id: string;
  name: string;
  types: string[];
  type?: string;
  startDate: Timestamp;
  endDate: Timestamp;
  registrationDeadline: Timestamp;
  earlyDeadline: Timestamp;
  midDeadline: Timestamp;
  earlyPrice: number;
  midPrice: number;
  latePrice: number;
  status: "open" | "closed";
  weightCategories: { male: string[]; female: string[] };
  imageUrl?: string;
}

function formatDate(ts: Timestamp, localeStr: string): string {
  const d = ts.toDate();
  if (localeStr === "ka") return formatDateGeorgian(d);
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export default function Dashboard() {
  const { t } = useTranslation();
  const locale = localStorage.getItem("language") || "en";
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [allCompetitions, setAllCompetitions] = useState<Competition[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<"open" | "closed">("open");
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
  const [activePackages, setActivePackages] = useState<CreditPackage[]>([]);
  const [searchText, setSearchText] = useState("");
  const [pastPage, setPastPage] = useState(1);
  const w = useWindowWidth();
  const isMobile = w < 768;

  useEffect(() => {
    const loadData = async () => {
      try {
        const [comps, packages] = await Promise.all([
          getAllCompetitions(),
          currentUser
            ? getCreditPackagesByCoach(currentUser.uid)
            : Promise.resolve([] as CreditPackage[]),
        ]);
        setAllCompetitions(comps as Competition[]);
        setActivePackages(
          packages.filter(
            (p) => p.status === "active" && p.creditsUsed < p.creditsTotal
          )
        );
      } catch (err) {
        console.error("Failed to load dashboard data:", err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [currentUser]);

  let competitions = allCompetitions.filter((c) =>
    activeTab === "open" ? c.status === "open" : c.status === "closed"
  );
  if (searchText.trim()) {
    const q = searchText.toLowerCase();
    competitions = competitions.filter((c) => c.name.toLowerCase().includes(q));
  }

  // ── Pagination for Past/Closed tab ────────────────────────────────────────
  const PAST_PAGE_SIZE = 10;
  const pastTotalPages = activeTab === "closed" ? Math.ceil(competitions.length / PAST_PAGE_SIZE) : 1;
  const pagedCompetitions = activeTab === "closed"
    ? competitions.slice((pastPage - 1) * PAST_PAGE_SIZE, pastPage * PAST_PAGE_SIZE)
    : competitions;

  // Reset page when tab or search changes
  useEffect(() => { setPastPage(1); }, [activeTab]);
  useEffect(() => { setPastPage(1); }, [searchText]);

  const openCount = allCompetitions.filter((c) => c.status === "open").length;

  const toggleDetails = (compId: string) => {
    setExpandedCards((prev) => {
      const next = new Set(prev);
      if (next.has(compId)) next.delete(compId);
      else next.add(compId);
      return next;
    });
  };

  /**
   * Returns the active credit package for a given competition, if any.
   * Checks for exact match OR "any" wildcard packages.
   */
  const getPackageForComp = (compId: string): CreditPackage | undefined => {
    return activePackages.find(
      (p) => p.competitionId === compId || p.competitionId === "any"
    );
  };

  return (
    <div style={s.page} className="page-content">
      <Navbar />
      <div style={s.container}>

        {/* ── Credit packages banner ──────────────────────────────────────── */}
        {activePackages.length > 0 && (
          <div style={s.creditsBanner}>
            <div style={s.creditsTitle}>
              💳 {t("dashboard.credits.bannerTitle")}
            </div>
            <div style={s.creditsList}>
              {activePackages.map((pkg) => (
                <div key={pkg.id} style={s.creditsItem}>
                  <span style={s.creditsCheck}>✓</span>
                  <span style={s.creditsItemText}>
                    <strong>{pkg.competitionName}:</strong>{" "}
                    {pkg.creditsTotal - pkg.creditsUsed}{" "}
                    {t("dashboard.credits.remaining")}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Header */}
        <div style={{ ...s.header, flexDirection: isMobile ? "column" : "row", gap: isMobile ? "12px" : undefined }}>
          <div>
            <div style={s.pageLabel}>{t("dashboard.pageLabel")}</div>
            <div style={{ ...s.heading, fontSize: isMobile ? "26px" : "36px" }}>{t("dashboard.heading")}</div>
            <div style={s.subheading}>{t("dashboard.subheading")}</div>
          </div>
          {!loading && (
            <div style={{ ...s.countBadge, alignSelf: isMobile ? "flex-start" : undefined }}>
              <span style={s.countNumber}>{openCount}</span>
              <span style={s.countLabel}>{t("dashboard.count.open")}</span>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div style={s.tabBar}>
          {(["open", "closed"] as const).map((tab) => (
            <button
              key={tab}
              style={activeTab === tab ? s.tabActive : s.tabInactive}
              onClick={() => setActiveTab(tab)}
            >
              {tab === "open" ? t("dashboard.tab.open") : t("dashboard.tab.past")}
              <span style={activeTab === tab ? s.tabCountActive : s.tabCount}>
                {tab === "open"
                  ? allCompetitions.filter((c) => c.status === "open").length
                  : allCompetitions.filter((c) => c.status === "closed").length}
              </span>
            </button>
          ))}
        </div>

        {/* Loading */}
        {loading && (
          <div style={s.stateBox}>
            <div style={s.stateText}>{t("dashboard.loading")}</div>
          </div>
        )}

        {/* Empty state */}
        {!loading && competitions.length === 0 && (
          <div style={s.stateBox}>
            <div style={s.emptyIcon}>🏆</div>
            <div style={s.stateText}>
              {activeTab === "open"
                ? t("dashboard.empty.title")
                : t("dashboard.past.empty.title")}
            </div>
            <div style={s.stateSub}>
              {activeTab === "open"
                ? t("dashboard.empty.sub")
                : t("dashboard.past.empty.sub")}
            </div>
          </div>
        )}

        {/* Search bar */}
        {!loading && (
          <div style={{ marginBottom: 20 }}>
            <input
              type="text"
              placeholder="Search competitions..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{
                width: "100%",
                padding: isMobile ? "9px 12px" : "10px 14px",
                fontSize: isMobile ? 13 : 14,
                borderRadius: 8,
                border: `1px solid ${C.border}`,
                backgroundColor: C.surface,
                color: C.text,
                outline: "none",
                fontFamily: FONT.body,
                boxSizing: "border-box",
              }}
            />
          </div>
        )}

        {/* Competition grid */}
        {!loading && competitions.length > 0 && (
          <div style={{ ...s.grid, gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)" }}>
            {pagedCompetitions.map((comp) => {
              const currentPrice = calculatePrice(comp);
              const isExpanded = expandedCards.has(comp.id);
              const typeList: string[] = comp.types ?? (comp.type ? [comp.type] : []);
              const compPackage = getPackageForComp(comp.id);
              const hasPrepaid = !!compPackage;

              // ── Mobile: horizontal compact card ──────────────────────
              if (isMobile) {
                return (
                  <div
                    key={comp.id}
                    style={{
                      backgroundColor: C.surface,
                      borderRadius: 10,
                      overflow: "hidden",
                      border: `1px solid ${C.border}`,
                      marginBottom: 12,
                    }}
                  >
                    {/* Top accent bar */}
                    <div style={{
                      height: 2,
                      background: comp.status === "open"
                        ? `linear-gradient(90deg, ${C.red}, rgba(218,41,28,0.4))`
                        : "rgba(0,0,0,0.08)",
                    }} />

                    <div style={{ display: "flex", flexDirection: "row", padding: "12px 10px", gap: 10 }}>
                      {/* Left image */}
                      {comp.imageUrl ? (
                        <div style={{
                          width: 80, height: 80, minWidth: 80, borderRadius: 8,
                          backgroundImage: `url(${comp.imageUrl})`,
                          backgroundSize: "cover", backgroundPosition: "center",
                          flexShrink: 0,
                        }} />
                      ) : (
                        <div style={{
                          width: 80, height: 80, minWidth: 80, borderRadius: 8,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          backgroundColor: comp.status === "open" ? "rgba(218,41,28,0.08)" : C.surface2,
                          border: `1px solid ${C.border}`,
                          flexShrink: 0,
                        }}>
                          <div style={{
                            fontFamily: FONT.display,
                            fontSize: 24,
                            color: comp.status === "open" ? C.red : C.text3,
                            opacity: 0.7,
                          }}>
                            🏆
                          </div>
                        </div>
                      )}

                      {/* Right content */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontFamily: FONT.body,
                          fontSize: 14,
                          fontWeight: 700,
                          color: C.text,
                          lineHeight: 1.3,
                          marginBottom: 4,
                        }}>
                          {comp.name}
                        </div>

                        {/* Type pills */}
                        {typeList.length > 0 && (
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 3, marginTop: 4 }}>
                            {typeList.map((type) => (
                              <span key={type} style={{
                                backgroundColor: C.surface2,
                                color: C.text2,
                                fontSize: 9,
                                fontWeight: 700,
                                padding: "2px 7px",
                                borderRadius: 20,
                                letterSpacing: "0.5px",
                                border: `1px solid ${C.border}`,
                              }}>{type}</span>
                            ))}
                            {hasPrepaid && (
                              <span style={{
                                backgroundColor: "var(--color-success-soft)",
                                color: "var(--color-success)",
                                fontSize: 9,
                                fontWeight: 700,
                                padding: "2px 7px",
                                borderRadius: 20,
                                border: "1px solid #6ee7b7",
                              }}>
                                💳 {t("dashboard.credits.available")}
                              </span>
                            )}
                          </div>
                        )}

                        {/* Dates */}
                        <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 4 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                            <span style={{ fontSize: 11, color: C.text2 }}>📅</span>
                            <span style={{ fontSize: 11, color: C.text2, fontWeight: 500, fontFamily: FONT.body }}>
                              {formatDate(comp.startDate, locale)} — {formatDate(comp.endDate, locale)}
                            </span>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                            <span style={{ fontSize: 11, color: C.text2 }}>⏰</span>
                            <span style={{
                              fontSize: 11,
                              color: comp.status === "open" ? C.red : C.text3,
                              fontWeight: comp.status === "open" ? 700 : 500,
                              fontFamily: FONT.body,
                            }}>
                              {formatDate(comp.registrationDeadline, locale)}
                            </span>
                          </div>
                        </div>

                        {/* Toggle + register */}
                        <div style={{ marginTop: 10, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <button
                            style={{
                              display: "inline-flex", alignItems: "center", gap: 4,
                              background: "none", border: `1px solid ${C.border2}`,
                              borderRadius: 6, padding: "4px 10px", fontSize: 10, fontWeight: 600,
                              color: C.text2, cursor: "pointer", fontFamily: FONT.body,
                            }}
                            onClick={() => toggleDetails(comp.id)}
                          >
                            <span>{isExpanded ? "▲" : "▼"} {isExpanded ? "Hide" : "Details"}</span>
                          </button>
                          {comp.status === "open" ? (
                            <button
                              style={{
                                display: "inline-flex", alignItems: "center", gap: 4,
                                backgroundColor: C.red, color: "#fff", border: "none",
                                borderRadius: 6, padding: "5px 10px", fontSize: 10,
                                fontWeight: 700, cursor: "pointer", fontFamily: FONT.body,
                              }}
                              onClick={() => navigate(`/register/${comp.id}`)}
                            >
                              {t("dashboard.registerBtn")}
                            </button>
                          ) : (
                            <div style={{
                              fontSize: 10, fontWeight: 600, color: C.text3,
                              backgroundColor: C.surface2, border: `1px solid ${C.border}`,
                              borderRadius: 6, padding: "5px 10px", fontFamily: FONT.body,
                            }}>
                              Registration Closed
                            </div>
                          )}
                        </div>

                        {/* Expanded details (mobile) */}
                        {isExpanded && (
                          <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 8 }}>
                            {hasPrepaid && compPackage && (
                              <div style={{
                                backgroundColor: "var(--color-success-soft)",
                                border: "1px solid #6ee7b7",
                                borderRadius: 7,
                                padding: "6px 10px",
                                fontSize: 11,
                                color: "var(--color-success)",
                                fontWeight: 600,
                                fontFamily: FONT.body,
                              }}>
                                ✓ {t("dashboard.credits.stripText").replace("{remaining}", String(compPackage.creditsTotal - compPackage.creditsUsed))}
                              </div>
                            )}
                            <div style={{
                              backgroundColor: C.surface2,
                              borderRadius: 8,
                              padding: "10px 12px",
                              border: `1px solid ${C.border}`,
                            }}>
                              <div style={{ fontSize: 10, fontWeight: 700, color: C.text3, textTransform: "uppercase", letterSpacing: "1px", marginBottom: 6 }}>
                                {t("dashboard.pricing.title")}
                              </div>
                              <div style={{ fontSize: 11, color: C.text2, display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                                <span>{t("dashboard.pricing.early")} — {t("admin.competitions.labels.until")} {formatDate(comp.earlyDeadline, locale)}</span>
                                <span>{comp.earlyPrice} GEL</span>
                              </div>
                              <div style={{ fontSize: 11, color: C.text2, display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                                <span>{t("dashboard.pricing.mid")} — {t("admin.competitions.labels.until")} {formatDate(comp.midDeadline, locale)}</span>
                                <span>{comp.midPrice} GEL</span>
                              </div>
                              <div style={{ fontSize: 11, color: C.text2, display: "flex", justifyContent: "space-between" }}>
                                <span>{t("dashboard.pricing.late")}</span>
                                <span>{comp.latePrice} GEL</span>
                              </div>
                              <div style={{ height: 1, backgroundColor: C.border, margin: "6px 0" }} />
                              <div style={{ fontSize: 12, fontWeight: 700, color: C.text, display: "flex", justifyContent: "space-between" }}>
                                <span>{t("dashboard.pricing.current")}</span>
                                <span style={{ color: C.red }}>{currentPrice} GEL</span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              }

              // ── Desktop: redesigned horizontal card (compact) ─────────────
              return (
                <div key={comp.id} style={s.deskCard}>
                  {/* Left side – image / placeholder */}
                  {comp.imageUrl ? (
                    <div style={{ ...s.deskCardImage, backgroundImage: `url(${comp.imageUrl})` }}>
                      {comp.status === "open" && currentPrice > 0 && (
                        <div style={s.deskImagePriceBadge}>{currentPrice} GEL</div>
                      )}
                    </div>
                  ) : (
                    <div style={{ ...s.deskCardPlaceholder, ...(comp.status === "open" ? s.placeholderOpen : s.placeholderClosed) }}>
                      <div style={{ color: comp.status === "open" ? "rgba(218,41,28,0.4)" : C.border2 }}>
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="5" r="2" />
                          <path d="M12 7v6l-3 3" /><path d="M12 13l3 3" />
                          <path d="M9 10H6" /><path d="M18 10h-3" />
                        </svg>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
                        <div style={{ ...s.statusBadge, ...(comp.status === "open" ? s.statusOpen : s.statusClosed) }}>
                          {comp.status === "open" ? t("admin.competitions.status.open") : t("admin.competitions.status.closed")}
                        </div>
                        {comp.status === "open" && currentPrice > 0 && (
                          <div style={s.deskImagePriceBadge}>{currentPrice} GEL</div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Right side – all content */}
                  <div style={s.deskCardBody}>
                    <div style={s.deskCompName}>{comp.name}</div>

                    <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 12 }}>
                      {typeList.map((type) => (
                        <span key={type} style={s.deskTypePill}>{type}</span>
                      ))}
                      {hasPrepaid && (
                        <span style={s.deskPrepaidBadge}>
                          💳 {t("dashboard.credits.available")}
                        </span>
                      )}
                    </div>

                    {hasPrepaid && compPackage && (
                      <div style={s.deskPrepaidStrip}>
                        ✓ {t("dashboard.credits.stripText").replace("{remaining}", String(compPackage.creditsTotal - compPackage.creditsUsed))}
                      </div>
                    )}

                    <div style={s.deskMetaBlock}>
                      <div style={s.deskMetaLine}>
                        <span style={s.deskMetaIcon}>📅</span>
                        <span style={s.deskMetaKey}>{t("dashboard.competition.dates")}</span>
                        <span style={s.deskMetaVal}>
                          {formatDate(comp.startDate, locale)} — {formatDate(comp.endDate, locale)}
                        </span>
                      </div>
                      <div style={s.deskMetaLine}>
                        <span style={s.deskMetaIcon}>⏰</span>
                        <span style={s.deskMetaKey}>{t("dashboard.competition.deadline")}</span>
                        <span style={{ ...s.deskMetaVal, color: comp.status === "open" ? C.red : C.text2, fontWeight: comp.status === "open" ? 700 : 500 }}>
                          {formatDate(comp.registrationDeadline, locale)}
                        </span>
                      </div>
                    </div>

                    <button
                      style={s.deskDetailsToggle}
                      onClick={() => toggleDetails(comp.id)}
                    >
                      <span>{isExpanded ? t("admin.competitions.hideDetails") : t("admin.competitions.showDetails")}</span>
                    </button>

                    {isExpanded && (
                      <div style={s.deskDetailsPanel}>
                        <div style={s.deskPricingBox}>
                          <div style={s.deskPricingTitle}>{t("dashboard.pricing.title")}</div>
                          <div style={s.deskPriceRow}>
                            <span>{t("dashboard.pricing.early")} — {t("admin.competitions.labels.until")} {formatDate(comp.earlyDeadline, locale)}</span>
                            <span>{comp.earlyPrice} GEL</span>
                          </div>
                          <div style={s.deskPriceRow}>
                            <span>{t("dashboard.pricing.mid")} — {t("admin.competitions.labels.until")} {formatDate(comp.midDeadline, locale)}</span>
                            <span>{comp.midPrice} GEL</span>
                          </div>
                          <div style={s.deskPriceRow}>
                            <span>{t("dashboard.pricing.late")}</span>
                            <span>{comp.latePrice} GEL</span>
                          </div>
                          <div style={s.deskPriceDivider} />
                          <div style={s.deskCurrentPriceRow}>
                            <span>{t("dashboard.pricing.current")}</span>
                            <span style={s.deskCurrentPriceAmount}>{currentPrice} GEL</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {comp.status === "open" ? (
                      <button style={s.deskRegisterBtn} onClick={() => navigate(`/register/${comp.id}`)}>
                        {t("dashboard.registerBtn")}
                      </button>
                    ) : (
                      <div style={s.deskClosedPill}>Registration Closed</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ── Past tab pagination ── */}
        {!loading && activeTab === "closed" && pastTotalPages > 1 && (
          <div style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 6,
            marginTop: 28,
            flexWrap: "wrap",
          }}>
            {Array.from({ length: pastTotalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => { setPastPage(page); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                style={{
                  minWidth: 36,
                  height: 36,
                  padding: "0 10px",
                  borderRadius: 8,
                  border: page === pastPage
                    ? `1px solid ${C.red}`
                    : `1px solid ${C.border2}`,
                  backgroundColor: page === pastPage ? C.red : C.surface,
                  color: page === pastPage ? "#fff" : C.text2,
                  fontSize: 13,
                  fontWeight: page === pastPage ? 700 : 500,
                  fontFamily: FONT.body,
                  cursor: "pointer",
                  transition: "background 0.15s, color 0.15s, border-color 0.15s",
                }}
              >
                {page}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  page: { minHeight: "100vh", backgroundColor: "transparent" },
  container: {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "40px 24px",
  },

  // Credits banner
  creditsBanner: {
    backgroundColor: "var(--color-success-soft)",
    border: "1px solid #6ee7b7",
    borderRadius: "12px",
    padding: "16px 20px",
    marginBottom: "24px",
  },
  creditsTitle: {
    fontSize: "13px",
    fontWeight: 700,
    color: "var(--color-success)",
    marginBottom: "10px",
  },
  creditsList: { display: "flex", flexDirection: "column", gap: "6px" },
  creditsItem: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "13px",
    color: "var(--color-success)",
  },
  creditsCheck: { fontWeight: 700, fontSize: "14px" },
  creditsItemText: { lineHeight: 1.4 },

  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "24px",
  },
  pageLabel: {
    fontSize: "10px",
    fontWeight: 700,
    letterSpacing: "2px",
    color: C.red,
    marginBottom: "6px",
    textTransform: "uppercase",
  },
  heading: {
    fontFamily: FONT.display,
    fontSize: "36px",
    letterSpacing: "2px",
    color: C.text,
    lineHeight: 1,
    marginBottom: "8px",
  },
  subheading: { fontSize: "14px", color: C.text2 },
  countBadge: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    backgroundColor: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: "12px",
    padding: "12px 20px",
    minWidth: "70px",
  },
  countNumber: {
    fontFamily: FONT.display,
    fontSize: "32px",
    color: C.red,
    lineHeight: 1,
  },
  countLabel: {
    fontSize: "10px",
    color: C.text2,
    letterSpacing: "1px",
    textTransform: "uppercase",
    marginTop: "2px",
  },
  tabBar: {
    display: "flex",
    gap: "4px",
    marginBottom: "28px",
    backgroundColor: C.surface2,
    borderRadius: "10px",
    padding: "4px",
    width: "fit-content",
    border: `1px solid ${C.border}`,
  },
  tabActive: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "8px 20px",
    backgroundColor: C.surface,
    color: C.text,
    border: `1px solid ${C.border}`,
    borderRadius: "7px",
    fontSize: "13px",
    fontWeight: 700,
    cursor: "pointer",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
  },
  tabInactive: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "8px 20px",
    backgroundColor: "transparent",
    color: C.text2,
    border: "none",
    borderRadius: "7px",
    fontSize: "13px",
    fontWeight: 500,
    cursor: "pointer",
  },
  tabCountActive: {
    backgroundColor: C.red,
    color: "#fff",
    borderRadius: "20px",
    padding: "1px 7px",
    fontSize: "11px",
    fontWeight: 700,
  },
  tabCount: {
    backgroundColor: C.border,
    color: C.text3,
    borderRadius: "20px",
    padding: "1px 7px",
    fontSize: "11px",
    fontWeight: 600,
  },
  stateBox: { textAlign: "center", padding: "80px 24px" },
  emptyIcon: { fontSize: "40px", marginBottom: "16px" },
  stateText: { fontSize: "16px", color: C.text2, marginBottom: "8px" },
  stateSub: { fontSize: "13px", color: C.text3 },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
    gap: "20px",
  },
  card: {
    backgroundColor: C.surface,
    borderRadius: "12px",
    border: `1px solid ${C.border}`,
    overflow: "hidden",
    transition: "transform 0.2s, box-shadow 0.2s",
  },
  cardWithCredits: {
    border: "1px solid #6ee7b7",
    boxShadow: "0 0 0 1px #6ee7b7",
  },
  cardImageWrapper: {
    width: "100%",
    height: "180px",
    overflow: "hidden",
  },
  cardImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  cardAccent: { height: "3px", background: C.red },
  cardAccentGreen: { height: "3px", background: C.success },
  cardBody: {
    padding: "20px 22px",
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  cardHeader: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  competitionName: {
    fontFamily: FONT.body,
    fontSize: "17px",
    fontWeight: 700,
    color: C.text,
    lineHeight: 1.3,
  },
  typeBadgeRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: "4px",
  },
  typeBadge: {
    backgroundColor: C.surface2,
    color: C.text2,
    fontSize: "10px",
    fontWeight: 700,
    padding: "3px 9px",
    borderRadius: "20px",
    letterSpacing: "0.8px",
    textTransform: "uppercase",
    border: `1px solid ${C.border}`,
  },
  prepaidBadge: {
    backgroundColor: "var(--color-success-soft)",
    color: "var(--color-success)",
    fontSize: "10px",
    fontWeight: 700,
    padding: "3px 9px",
    borderRadius: "20px",
    letterSpacing: "0.5px",
    border: "1px solid #6ee7b7",
  },
  prepaidStrip: {
    backgroundColor: "var(--color-success-soft)",
    border: "1px solid #6ee7b7",
    borderRadius: "7px",
    padding: "7px 12px",
    fontSize: "12px",
    color: "var(--color-success)",
    fontWeight: 600,
  },
  infoRow: {
    display: "flex",
    gap: "8px",
    fontSize: "13px",
    color: C.text2,
    alignItems: "center",
  },
  infoIcon: { fontSize: "12px" },
  infoLabel: {
    fontWeight: 600,
    color: C.text2,
    minWidth: "80px",
    fontSize: "12px",
    letterSpacing: "0.3px",
  },
  infoValue: { color: C.text, fontSize: "13px" },
  detailsToggleBtn: {
    backgroundColor: "transparent",
    border: `1px solid ${C.border2}`,
    borderRadius: "7px",
    padding: "7px 14px",
    fontSize: "12px",
    color: C.text2,
    cursor: "pointer",
    fontWeight: 600,
    alignSelf: "flex-start",
  },
  expandedSection: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    paddingTop: "10px",
    borderTop: `1px solid ${C.border}`,
  },
  pricingBox: {
    backgroundColor: C.surface2,
    borderRadius: "8px",
    padding: "14px 16px",
    display: "flex",
    flexDirection: "column",
    gap: "7px",
    border: `1px solid ${C.border}`,
  },
  pricingTitle: {
    fontSize: "10px",
    fontWeight: 700,
    color: C.text3,
    textTransform: "uppercase",
    letterSpacing: "1px",
    marginBottom: "2px",
  },
  priceRow: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: "12px",
    color: C.text2,
  },
  priceDivider: {
    height: "1px",
    backgroundColor: C.border,
    margin: "2px 0",
  },
  currentPriceRow: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: "13px",
    fontWeight: 600,
    color: C.text,
  },
  currentPriceAmount: { color: C.red, fontWeight: 700 },
  registerBtn: {
    marginTop: "4px",
    backgroundColor: C.red,
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    padding: "12px",
    fontSize: "14px",
    fontWeight: 700,
    cursor: "pointer",
    letterSpacing: "0.3px",
  },
  closedPill: {
    marginTop: "4px",
    backgroundColor: C.surface2,
    color: C.text3,
    border: `1px solid ${C.border}`,
    borderRadius: "8px",
    padding: "12px",
    fontSize: "13px",
    fontWeight: 600,
    textAlign: "center",
  },

  // ── New horizontal desktop card styles ─────────────────────────────────────
  deskCard: {
    display: "flex",
    flexDirection: "row" as const,
    backgroundColor: C.surface,
    borderRadius: 14,
    overflow: "hidden",
    boxShadow: C.shadow,
    border: `1px solid ${C.border}`,
    transition: "transform 0.25s cubic-bezier(.22,1,.36,1), box-shadow 0.25s",
    maxWidth: 620,
    margin: "0 auto",
  },
  deskCardImage: {
    width: 130,
    flexShrink: 0,
    backgroundSize: "contain",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    backgroundColor: C.surface,
    position: "relative",
  },
  deskCardImageDim: {
    position: "absolute",
    inset: 0,
    background: "linear-gradient(135deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.6) 100%)",
  },
  deskImagePriceBadge: {
    position: "absolute",
    bottom: 10,
    left: 10,
    backgroundColor: "rgba(218,41,28,0.9)",
    backdropFilter: "blur(4px)",
    color: "#fff",
    borderRadius: 20,
    padding: "2px 8px",
    fontSize: 10,
    fontWeight: 700,
    border: "1px solid rgba(255,255,255,0.2)",
    fontFamily: FONT.body,
  },
  deskCardPlaceholder: {
    width: 130,
    flexShrink: 0,
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    backgroundColor: C.surface2,
  },
  placeholderOpen: {
    background: "linear-gradient(135deg, rgba(218,41,28,0.08) 0%, rgba(218,41,28,0.03) 100%)",
    borderBottom: `1px solid ${C.border}`,
  },
  placeholderClosed: {
    background: C.surface2,
    borderBottom: `1px solid ${C.border}`,
  },
  statusBadge: {
    fontSize: 9,
    fontWeight: 800,
    padding: "3px 9px",
    borderRadius: 20,
    letterSpacing: "0.7px",
    textTransform: "uppercase" as const,
    fontFamily: FONT.body,
  },
  statusOpen: {
    backgroundColor: "rgba(16,185,129,0.15)",
    color: C.success,
    border: "1px solid rgba(16,185,129,0.3)",
  },
  statusClosed: {
    backgroundColor: C.surface2,
    color: C.text3,
    border: `1px solid ${C.border}`,
  },
  deskCardBody: {
    flex: 1,
    padding: "18px 20px",
    display: "flex",
    flexDirection: "column" as const,
    gap: 10,
    backgroundColor: C.surface,
  },
  deskCompName: {
    fontFamily: FONT.display,
    fontSize: 20,
    letterSpacing: "0.5px",
    color: C.text,
    lineHeight: 1.2,
    marginBottom: 4,
  },
  deskTypePill: {
    fontSize: 10,
    padding: "2px 10px",
    backgroundColor: "rgba(218,41,28,0.12)",
    color: "#da291c",
    borderRadius: 20,
    fontWeight: 700,
    letterSpacing: "0.5px",
    border: "1px solid rgba(218,41,28,0.2)",
    textTransform: "uppercase" as const,
    fontFamily: FONT.body,
  },
  deskPrepaidBadge: {
    backgroundColor: "var(--color-success-soft)",
    color: "var(--color-success)",
    fontSize: 10,
    fontWeight: 700,
    padding: "2px 10px",
    borderRadius: 20,
    border: "1px solid #6ee7b7",
  },
  deskPrepaidStrip: {
    backgroundColor: "var(--color-success-soft)",
    border: "1px solid #6ee7b7",
    borderRadius: 7,
    padding: "7px 12px",
    fontSize: 11,
    color: "var(--color-success)",
    fontWeight: 600,
  },
  deskMetaBlock: {
    display: "flex",
    flexDirection: "column" as const,
    gap: 8,
    marginBottom: 2,
    padding: "8px 0",
  },
  deskMetaLine: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  deskMetaIcon: {
    color: C.text3,
    flexShrink: 0,
  },
  deskMetaKey: {
    fontSize: 10,
    fontWeight: 700,
    color: C.text2,
    textTransform: "uppercase" as const,
    letterSpacing: "0.6px",
    minWidth: 72,
    fontFamily: FONT.body,
  },
  deskMetaVal: {
    fontSize: 12,
    color: C.text,
    fontWeight: 500,
    fontFamily: FONT.body,
  },
  deskDetailsToggle: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    background: "none",
    border: `1px solid ${C.border}`,
    borderRadius: 7,
    padding: "5px 12px",
    fontSize: 11,
    fontWeight: 600,
    color: C.text2,
    cursor: "pointer",
    fontFamily: FONT.body,
    alignSelf: "flex-start",
    transition: "border-color 0.15s, color 0.15s",
    marginBottom: 4,
  },
  deskDetailsPanel: {
    display: "flex",
    flexDirection: "column" as const,
    gap: 12,
    paddingTop: 4,
  },
  deskPricingBox: {
    backgroundColor: C.surface2,
    borderRadius: 9,
    padding: "12px 14px",
    display: "flex",
    flexDirection: "column",
    gap: 8,
    border: `1px solid ${C.border}`,
  },
  deskPricingTitle: {
    fontSize: 9,
    fontWeight: 800,
    color: C.text3,
    textTransform: "uppercase",
    letterSpacing: "1px",
    marginBottom: 4,
  },
  deskPriceRow: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: 11,
    color: C.text2,
  },
  deskPriceDivider: {
    height: 1,
    backgroundColor: C.border,
    margin: "4px 0",
  },
  deskCurrentPriceRow: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: 12,
    fontWeight: 700,
    color: C.text,
  },
  deskCurrentPriceAmount: {
    color: C.red,
    fontWeight: 800,
  },
  deskRegisterBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    padding: "10px 16px",
    backgroundColor: C.red,
    color: "#fff",
    border: "none",
    borderRadius: 9,
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: FONT.body,
    width: "100%",
    letterSpacing: "0.4px",
    transition: "opacity 0.15s, transform 0.15s",
    marginTop: 4,
  },
  deskClosedPill: {
    marginTop: 4,
    backgroundColor: C.surface2,
    color: C.text3,
    border: `1px solid ${C.border}`,
    borderRadius: 9,
    padding: "10px 16px",
    fontSize: 13,
    fontWeight: 600,
    textAlign: "center",
  },
};
